const STORAGE_CURVE_POINTS = 6

function round(value, digits = 3) {
  const base = Math.pow(10, digits)
  return Math.round(Number(value) * base) / base
}

function buildLevelStorageCurve(levels, totalCapacity) {
  const storages = levels.map((_, index) => {
    const ratio = levels.length <= 1 ? 1 : index / (levels.length - 1)
    return round(totalCapacity * Math.pow(ratio, 1.35), 4)
  })
  return { levels, storages }
}

function buildDischargeCurve(levels, maxDischarge) {
  const max_discharges = levels.map((_, index) => {
    const ratio = levels.length <= 1 ? 1 : index / (levels.length - 1)
    return round(maxDischarge * Math.pow(ratio, 1.18), 1)
  })
  return { levels, max_discharges }
}

function deriveModelCurves(levels, capacities, dischargeCapacity) {
  const minLevel = levels.dead_level - Math.max(4, (levels.normal_level - levels.dead_level) * 0.45)
  const maxLevel = levels.check_flood_level
  const curveLevels = Array.from({ length: STORAGE_CURVE_POINTS }, (_, index) => {
    const ratio = STORAGE_CURVE_POINTS <= 1 ? 1 : index / (STORAGE_CURVE_POINTS - 1)
    return round(minLevel + (maxLevel - minLevel) * ratio, 2)
  })
  return {
    level_storage: buildLevelStorageCurve(curveLevels, capacities.total_capacity),
    discharge_capacity: buildDischargeCurve(curveLevels, dischargeCapacity),
  }
}

function reservoir(definition) {
  const curves = definition.curves || deriveModelCurves(
    definition.characteristic_levels,
    definition.capacities,
    definition.discharge_capacity_m3s || 12000
  )
  return {
    ...definition,
    curves,
  }
}

export const RESERVOIR_CATALOG = {
  guxian: reservoir({
    id: 'guxian',
    name: '古贤水利枢纽',
    aliases: ['古贤水库', '古贤工程', '古贤枢纽', '黄河古贤'],
    river: '黄河干流',
    reservoir_type: '大型水利枢纽',
    location: [110.4518, 36.0662, 450],
    sourceLevel: 'project_public_core_with_demo_levels',
    sourceNote: '总库容、调节库容、拦沙库容、装机容量沿用项目已固化工程指标；特征水位按当前演示水位体系补齐，供调度模型稳定运行。',
    coordinateNote: '按公开工程区位"黄河中游晋陕峡谷、壶口瀑布下游约十余公里"结合在线地图河槽位置校准',
    catchment_area_km2: 489900,
    characteristic_levels: {
      dead_level: 610.5,
      normal_level: 617.2,
      flood_limit_level: 616.2,
      design_flood_level: 626.0,
      check_flood_level: 627.5,
    },
    capacities: {
      total_capacity: 133.0,
      flood_capacity: 34.6,
      regulation_capacity: 34.6,
      water_sediment_capacity: 35.0,
      sediment_capacity: 93.4,
    },
    engineering: {
      installed_capacity_mw: 2100,
      average_annual_generation_100m_kwh: 63.45,
      main_function: '防洪、减淤、供水、发电、生态调度',
    },
    flood_standard: {
      design: '1000 年一遇',
      check: '10000 年一遇',
    },
    discharge_capacity_m3s: 30000,
    snapshot: { level: 617.2, inflow: 1203, outflow: 940 },
  }),

  xuefeng: reservoir({
    id: 'xuefeng',
    name: '薛峰水库',
    aliases: ['薛峰', '薛峰站', '薛峰水文站'],
    river: '澽水',
    reservoir_type: '中型水库',
    location: [110.3073384, 35.5304201, 450],
    sourceLevel: 'public_basic_with_demo_levels',
    sourceNote: '公开资料可核到正常库容3000万m³、集雨面积529km²、海拔592m；特征水位按平台已有薛峰演示水位补齐。',
    coordinateNote: '配水工程调蓄设施公开坐标有限，当前采用演示规划坐标',
    catchment_area_km2: 529,
    characteristic_levels: {
      dead_level: 905.0,
      normal_level: 913.5,
      flood_limit_level: 912.2,
      design_flood_level: 918.0,
      check_flood_level: 920.0,
    },
    capacities: {
      total_capacity: 0.32,
      normal_capacity: 0.30,
      flood_capacity: 0.08,
      regulation_capacity: 0.24,
    },
    engineering: {
      built_year: 1973,
      main_function: '防洪、灌溉、供水调蓄',
    },
    flood_standard: {
      design: '50 年一遇',
      check: '100 年一遇',
    },
    discharge_capacity_m3s: 2600,
    snapshot: { level: 912.98, inflow: 167, outflow: 146 },
  }),

  longtan: reservoir({
    id: 'longtan',
    name: '龙潭水库',
    aliases: ['龙潭'],
    river: '渭河支流',
    reservoir_type: '中型水库',
    location: [108.8558869, 34.9074351, 450],
    sourceLevel: 'demo_estimated',
    sourceNote: '同名水库较多，当前地图点缺可靠公开特征水位；按中型山区水库展示参数补齐，集雨面积参照渭北支流典型值估算。',
    coordinateNote: '配水工程沿线水库公开坐标有限，当前采用演示规划坐标',
    catchment_area_km2: 186,
    characteristic_levels: {
      dead_level: 602.0,
      normal_level: 618.0,
      flood_limit_level: 616.0,
      design_flood_level: 624.0,
      check_flood_level: 626.0,
    },
    capacities: {
      total_capacity: 0.86,
      flood_capacity: 0.22,
      regulation_capacity: 0.48,
    },
    engineering: {
      built_year: 1985,
      main_function: '防洪、灌溉、生态补水',
    },
    flood_standard: {
      design: '50 年一遇',
      check: '200 年一遇',
    },
    discharge_capacity_m3s: 5200,
    snapshot: { level: 616.4, inflow: 308, outflow: 226 },
  }),

  dongzhuang: reservoir({
    id: 'dongzhuang',
    name: '东庄水库',
    aliases: ['东庄', '东庄水利枢纽', '泾河东庄'],
    river: '泾河',
    reservoir_type: '大型水利枢纽',
    location: [108.4983725, 34.7307591, 450],
    sourceLevel: 'public_planning_core_with_demo_levels',
    sourceNote: '按泾河东庄大型水利枢纽展示口径固化总库容32.76亿m³；特征水位和泄流能力按模型演示曲线补齐，集雨面积取泾河流域控制面积约4.3万km²。',
    coordinateNote: '按泾河东庄坝址公开区位结合在线地图河槽位置校准',
    catchment_area_km2: 43126,
    characteristic_levels: {
      dead_level: 775.0,
      normal_level: 789.0,
      flood_limit_level: 786.5,
      design_flood_level: 795.0,
      check_flood_level: 798.0,
    },
    capacities: {
      total_capacity: 32.76,
      flood_capacity: 12.0,
      regulation_capacity: 8.5,
      sediment_capacity: 18.0,
    },
    engineering: {
      installed_capacity_mw: 900,
      average_annual_generation_100m_kwh: 20.5,
      main_function: '防洪、减淤、供水、生态调度',
    },
    flood_standard: {
      design: '1000 年一遇',
      check: '2000 年一遇',
    },
    discharge_capacity_m3s: 18000,
    snapshot: { level: 786.8, inflow: 860, outflow: 520 },
  }),

  xiaodaokou: reservoir({
    id: 'xiaodaokou',
    name: '小道口水库',
    aliases: ['小道口'],
    river: '渭河支流',
    reservoir_type: '小型水库',
    location: [108.943887, 34.762381, 450],
    sourceLevel: 'demo_estimated',
    sourceNote: '未检索到稳定公开工程特征值，按小型调蓄水库展示参数补齐，集雨面积参照渭北支沟典型值估算。',
    coordinateNote: '配水工程沿线小型水库公开坐标有限，当前采用演示规划坐标',
    catchment_area_km2: 42,
    characteristic_levels: {
      dead_level: 552.0,
      normal_level: 560.5,
      flood_limit_level: 559.0,
      design_flood_level: 565.0,
      check_flood_level: 566.5,
    },
    capacities: {
      total_capacity: 0.18,
      flood_capacity: 0.05,
      regulation_capacity: 0.11,
    },
    engineering: {
      built_year: 1992,
      main_function: '灌溉调蓄、支沟防洪',
    },
    flood_standard: {
      design: '30 年一遇',
      check: '100 年一遇',
    },
    discharge_capacity_m3s: 950,
    snapshot: { level: 559.4, inflow: 62, outflow: 38 },
  }),

  shangshu: reservoir({
    id: 'shangshu',
    name: '尚书水库',
    aliases: ['尚书'],
    river: '渭河支流',
    reservoir_type: '小型水库',
    location: [109.24192, 34.920866, 450],
    sourceLevel: 'demo_estimated',
    sourceNote: '未检索到稳定公开工程特征值，按小型山区水库展示参数补齐，集雨面积参照渭北丘陵区典型值估算。',
    coordinateNote: '配水工程沿线小型水库公开坐标有限，当前采用演示规划坐标',
    catchment_area_km2: 58,
    characteristic_levels: {
      dead_level: 585.0,
      normal_level: 594.0,
      flood_limit_level: 592.5,
      design_flood_level: 599.0,
      check_flood_level: 600.5,
    },
    capacities: {
      total_capacity: 0.22,
      flood_capacity: 0.06,
      regulation_capacity: 0.13,
    },
    engineering: {
      built_year: 1988,
      main_function: '灌溉、乡镇供水、生态补水',
    },
    flood_standard: {
      design: '30 年一遇',
      check: '100 年一遇',
    },
    discharge_capacity_m3s: 1100,
    snapshot: { level: 592.8, inflow: 74, outflow: 45 },
  }),

  wuyi: reservoir({
    id: 'wuyi',
    name: '五一水库',
    aliases: ['五一'],
    river: '渭河支流',
    reservoir_type: '中型水库',
    location: [109.92489, 35.309226, 450],
    sourceLevel: 'demo_estimated',
    sourceNote: '同名水库较多，当前地图点缺可靠公开特征水位；按中型调蓄水库展示参数补齐，集雨面积参照渭北中游支流典型值估算。',
    coordinateNote: '配水工程沿线中型水库公开坐标有限，当前采用演示规划坐标',
    catchment_area_km2: 215,
    characteristic_levels: {
      dead_level: 612.0,
      normal_level: 623.0,
      flood_limit_level: 621.5,
      design_flood_level: 628.0,
      check_flood_level: 630.0,
    },
    capacities: {
      total_capacity: 0.38,
      flood_capacity: 0.10,
      regulation_capacity: 0.22,
    },
    engineering: {
      built_year: 1978,
      main_function: '防洪、灌溉、供水备用',
    },
    flood_standard: {
      design: '50 年一遇',
      check: '200 年一遇',
    },
    discharge_capacity_m3s: 1800,
    snapshot: { level: 621.9, inflow: 118, outflow: 76 },
  }),

  xijialing: reservoir({
    id: 'xijialing',
    name: '喜家岭调蓄水库',
    aliases: ['喜家岭', '喜家岭水库'],
    river: '配水工程支线',
    reservoir_type: '调蓄水库',
    location: [110.248509, 36.065675, 450],
    sourceLevel: 'demo_estimated',
    sourceNote: '调蓄设施公开参数有限，按输配水调蓄水库展示参数补齐，库容参照古贤受水区配水调蓄池典型规模估算。',
    coordinateNote: '配水工程调蓄设施公开坐标有限，当前采用演示规划坐标',
    catchment_area_km2: 5.2,
    characteristic_levels: {
      dead_level: 905.0,
      normal_level: 913.5,
      flood_limit_level: 912.0,
      design_flood_level: 918.0,
      check_flood_level: 920.0,
    },
    capacities: {
      total_capacity: 0.0062,
      flood_capacity: 0.0017,
      regulation_capacity: 0.0045,
    },
    engineering: {
      main_function: '城镇供水与农业灌溉调蓄',
    },
    flood_standard: {
      design: '50 年一遇',
      check: '100 年一遇',
    },
    discharge_capacity_m3s: 120,
    snapshot: { level: 912.7, inflow: 12, outflow: 9 },
  }),
}

export const RESERVOIR_SEQUENCE = [
  'guxian',
  'xuefeng',
  'longtan',
  'dongzhuang',
  'xiaodaokou',
  'shangshu',
  'wuyi',
  'xijialing',
]

export function listReservoirs() {
  return RESERVOIR_SEQUENCE.map(id => RESERVOIR_CATALOG[id])
}

export function getReservoirById(id = 'guxian') {
  return RESERVOIR_CATALOG[id] || RESERVOIR_CATALOG.guxian
}

export function findReservoirByName(text = '') {
  const query = String(text)
  return listReservoirs().find(item =>
    item.name === query || item.aliases.some(alias => query.includes(alias))
  ) || RESERVOIR_CATALOG.guxian
}

export function toTankenReservoirConfig(reservoirLike = 'guxian') {
  const item = typeof reservoirLike === 'string' ? getReservoirById(reservoirLike) : reservoirLike
  return {
    reservoir: {
      id: item.id,
      name: item.name,
      characteristic_levels: { ...item.characteristic_levels },
      capacities: {
        total_capacity: item.capacities.total_capacity,
        flood_capacity: item.capacities.flood_capacity,
      },
      curves: {
        level_storage: {
          levels: [...item.curves.level_storage.levels],
          storages: [...item.curves.level_storage.storages],
        },
        discharge_capacity: {
          levels: [...item.curves.discharge_capacity.levels],
          max_discharges: [...item.curves.discharge_capacity.max_discharges],
        },
      },
    },
    snapshot: {
      level: item.snapshot.level,
      inflow: item.snapshot.inflow,
    },
    metadata: {
      source_level: item.sourceLevel,
      source_note: item.sourceNote,
      aliases: item.aliases,
      location: item.location,
      engineering: item.engineering,
      capacities: item.capacities,
    },
  }
}
